import React, { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, FileText, PlusCircle, CreditCard, Shield, List, User, Mail, Eye, Edit, Trash2, Languages, CheckCircle, Lock } from 'lucide-react';
import Swal from 'sweetalert2';
import { getVersion } from './versionService';
import API_BASE from './apiBase';
import VersionManagementModal from './VersionManagementModal';
import formatDisplayLabel from './formatDisplayLabel';

// Mock styles object
const styles = {
  container: { minHeight: '100vh', background: '#f8fafc' },
  createBtn: {
    background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
    color: '#fff',
    border: 'none',
    borderRadius: '12px',
    padding: '0.75rem 1.5rem',
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    boxShadow: '0 4px 12px rgba(37,99,235,0.2)',
  },
  backBtn: {
    background: '#fff',
    color: '#2563eb',
    border: '2px solid #e2e8f0',
    borderRadius: '12px',
    padding: '0.6rem 1rem',
    fontSize: '0.95rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
  actionBtn: {
    background: '#fff',
    border: '2px solid #e2e8f0',
    borderRadius: 10,
    padding: 8,
    cursor: 'pointer',
  },
  primaryBtn: {
    padding: '0.5rem 1.25rem',
    border: 'none',
    borderRadius: 8,
    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
    color: '#fff',
    fontWeight: 700,
    cursor: 'pointer',
  },
  dangerBtn: {
    padding: '0.5rem 1rem',
    border: 'none',
    borderRadius: 8,
    background: '#ef4444',
    color: '#fff',
    fontWeight: 700,
    cursor: 'pointer',
  },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  modal: { background: '#fff', width: '90%', maxWidth: 520, maxHeight: '90vh', overflow: 'auto' },
  modalHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', padding: '1rem' },
  modalContent: { padding: '1.5rem' },
  modalActions: { display: 'flex', justifyContent: 'flex-end', gap: 12, padding: '1rem', borderTop: '1px solid #e2e8f0' },
  textarea: { width: '100%', padding: 8, border: '1px solid #cbd5e1', borderRadius: 6, fontFamily: 'inherit', resize: 'vertical' },
  cancelBtn: { padding: '0.5rem 1rem', border: '1px solid #cbd5e1', borderRadius: 8, background: '#fff', cursor: 'pointer' },
  saveBtn: { padding: '0.5rem 1.5rem', border: 'none', borderRadius: 8, background: '#667eea', color: '#fff', cursor: 'pointer', fontWeight: 600 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1.5rem', marginTop: '1.5rem' },
  card: { background: '#fff', borderRadius: 16, padding: '1.25rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', transition: 'all 0.2s ease', position: 'relative', overflow: 'hidden', boxSizing: 'border-box' },
  cardIcon: { marginBottom: 12 },
  iconWrapper: { display: 'flex', alignItems: 'center', justifyContent: 'center', width: 60, height: 60, borderRadius: 16, margin: '1.4rem auto 12px auto' },
  cardActions: { position: 'absolute', top: 10, right: 10, display: 'flex', gap: 6, transition: 'opacity 160ms ease, transform 160ms ease', opacity: 0, zIndex: 10 },
  actionSmall: { background: '#fff', border: '1.5px solid #e2e8f0', borderRadius: 8, padding: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  cardTitle: { fontSize: '1.1rem', fontWeight: 600, color: '#0f172a', marginBottom: 8, textAlign: 'center', wordBreak: 'break-word', overflowWrap: 'anywhere', wordWrap: 'break-word', minWidth: 0, maxWidth: '100%' },
  cardDesc: { fontSize: '0.875rem', color: '#64748b', lineHeight: 1.5, textAlign: 'center' },
  error: { color: '#dc2626', padding: '1rem', background: '#fee2e2', borderRadius: 8 }
};

// const formatDisplayLabel = (value) => {
//   const raw = String(value ?? '').trim();
//   if (!raw) return '';
//   return raw
//     .replace(/[_-]+/g, ' ')
//     .replace(/\s+/g, ' ')
//     .split(' ')
//     .filter(Boolean)
//     .map(word => word.length <= 3 ? word.toUpperCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
//     .join(' ');
// };
// function SubCategoryConsents({ category = 'Banking', tenantId = null,  token = '', onBack = null, onOpenSubMultilingual = null }) {
function SubCategoryConsents({ category = 'Banking', parentConsentId = null, parentConsentLabel = '', tenantId = null, token = '', onBack = null, onOpenSubMultilingual = null }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [newIcon, setNewIcon] = useState('FileText');
  const [newColor, setNewColor] = useState('#0070f3');
  const [newVersion, setNewVersion] = useState('');
  const [newPercent, setNewPercent] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [hoveredKey, setHoveredKey] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewItem, setViewItem] = useState(null);
  const [version, setVersion] = useState('...');
  const [versionItem, setVersionItem] = useState(null);
  // Use centralized API_BASE resolver (handles runtime overrides and comma-separated values)
  // fall back to a safe local default if apiBase returns empty
  const runtimeBase = (API_BASE || '').toString().trim() || 'http://localhost:8000';

  const fetchRows = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const q = new URLSearchParams();
      if (tenantId) q.set('tenant_id', String(tenantId));
      if (category) {
        // send normalized consent_type so server matches by name/sub_category
        q.set('consent_type', String(category).trim().toLowerCase());
      }
      if (parentConsentId != null) q.set('parent_consent_id', String(parentConsentId));
      const url = `${runtimeBase.replace(/\/$/, '')}/manager/sub-category-consents` + (q.toString() ? `?${q.toString()}` : '');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;
      const res = await fetch(url, { method: 'GET', headers });
      const body = await res.json().catch(() => null);
      if (!res.ok) throw new Error((body && (body.detail || body.message)) || `Failed to fetch (${res.status})`);

      const source = Array.isArray(body) ? body : (body && body.rows) ? body.rows : [];
      const mapped = source.map(r => {
        let parsed = {};
        try { parsed = typeof r.data === 'string' ? JSON.parse(r.data) : (r.data || {}); } catch (e) { parsed = r.data || {}; }
        const kind = parsed.kind || 'sub_consent';
        return {
          id: r.id || r._id || `${r.sub_category || r.name || Date.now()}`,
          consent_id: r.consent_id || r.parent_consent_id || parentConsentId,
          parent_consent_id: r.parent_consent_id || r.consent_id || parentConsentId,
          sub_consent_id: r.sub_consent_id || r.id,
          consent_type: r.consent_type || parsed.parent_consent_type || category || '',
          sub_category: r.sub_category || parsed.sub_consent_name || parsed.title || '',
          consent_message: r.message || r.consent_message || r.consent || r.description || '',
          icon: parsed.icon || r.icon || 'FileText',
          color: parsed.color || r.color || '#0070f3',
          version: parsed.version || r.version || '',
          status: parsed.status || r.status || 'under_process',
          locked: Boolean(parsed.locked || String(parsed.status || r.status || '').toLowerCase() === 'accepted'),
          notified_percent: parsed.notified_percent ?? (r.notified_percent ?? r.notified ?? 0),
          parsed,
          parent_consent_label: parsed.parent_consent_label || parentConsentLabel || category,
          kind,
          raw: r
        };
      }).filter(item => {
        if (item.kind === 'multilingual_consent' || item.parsed?.is_multilingual_variant) return false;
        return true;
      });
      setRows(mapped);
    } catch (e) {
      console.error('Failed to fetch sub-categories, using demo data:', e);
      setError(String(e.message || e));
      const bankDummy = [
        { id: `bank-acct`, consent_type: 'Account', sub_category: 'Account Details', consent_message: 'Account number, account type, branch details.', color: '#0070f3', notified_percent: 34 },
        { id: `bank-trans`, consent_type: 'Transaction', sub_category: 'Transaction History', consent_message: 'Transaction amounts, dates, and merchant info.', color: '#0070f3', notified_percent: 22 },
        { id: `bank-card`, consent_type: 'Card', sub_category: 'Card Details', consent_message: 'Card number (masked), expiry, card type.', color: '#0070f3', notified_percent: 18 },
      ];
      setRows(bankDummy);
    } finally {
      setLoading(false);
    }
  }, [runtimeBase, category, parentConsentId, parentConsentLabel, tenantId, token]);

  useEffect(() => {
    fetchRows();
    let mounted = true;
    (async () => {
      try {
        const v = await getVersion();
        if (mounted) setVersion(v);
      } catch (e) { }
    })();
    return () => { mounted = false; };
  }, [fetchRows]);

  const previewText = (html, max = 140) => {
    try {
      const d = document.createElement('div');
      d.innerHTML = html || '';
      const text = d.textContent || d.innerText || '';
      if (text.length <= max) return text;
      return text.slice(0, max - 1).trim() + '…';
    } catch (e) { return String(html || ''); }
  };

  const acceptVersion = async (item) => {
    const confirmed = await Swal.fire({ title: `Accept & Lock v${item.version || '1.0.0'}?`, text: 'This version cannot be edited after acceptance.', icon: 'question', showCancelButton: true, confirmButtonText: 'Accept & Lock', confirmButtonColor: '#059669' });
    if (!confirmed.isConfirmed) return;
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch(`${runtimeBase.replace(/\/$/, '')}/manager/accept-subcategory-version`, { method: 'POST', headers, body: JSON.stringify({ id: item.id, tenant_id: tenantId }) });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body?.detail || 'Failed to accept sub-consent');
      await fetchRows();
      await Swal.fire({ icon: 'success', title: 'Accepted & Locked', text: 'This consent version is now locked.', timer: 1600, showConfirmButton: false });
    } catch (e) {
      await Swal.fire({ icon: 'error', title: 'Accept Failed', text: e.message || 'Failed to accept sub-consent' });
    }
  };

  const startNextVersion = (item) => {
    const parts = String(item.version || '1.0.0').replace(/^v/i, '').split('.').map(Number);
    parts[parts.length - 1] = (parts[parts.length - 1] || 0) + 1;
    setNewName(item.sub_category || item.consent_type || '');
    setNewMessage(item.consent_message || '');
    setNewIcon(item.icon || 'FileText');
    setNewColor(item.color || '#0070f3');
    setNewVersion(parts.join('.'));
    setIsEditing(false);
    setShowNew(true);
  };

  return (
    <div style={styles.container}>
      <div style={{
        background: 'linear-gradient(135deg, #4c6af1ff )',
        padding: '0rem 3.5rem',
        borderRadius: '0 0 24px 24px',
        marginBottom: '1.5rem',
        boxShadow: '0 8px 32px rgba(102, 126, 234, 0.2)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
              <h1 style={{
                margin: 0,
                color: '#fff',
                fontSize: '2rem',
                fontWeight: 700,
                letterSpacing: '-0.02em'
              }}>
                {parentConsentLabel || (category ? formatDisplayLabel(category) : 'Sub-category Consents')}
              </h1>
              <span style={{
                background: 'rgba(255,255,255,0.25)',
                color: '#fff',
                padding: '0.35rem 0.85rem',
                borderRadius: '12px',
                fontSize: '1rem',
                fontWeight: 700,
                border: '2px solid rgba(255,255,255,0.3)',
                minWidth: '42px',
                textAlign: 'center'
              }}>
                {rows.length}
              </span>
            </div>
            <p style={{
              margin: '0.5rem 0 0 0',
              color: 'rgba(255,255,255,0.85)',
              fontSize: '0.95rem',
              fontWeight: 400
            }}>
              Manage and configure consent sub-categories • {rows.length} total items
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <button
              style={{
                background: '#fff',
                color: '#667eea',
                border: 'none',
                borderRadius: '12px',
                padding: '0.75rem 1.5rem',
                fontSize: '0.95rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                transition: 'all 0.2s ease',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.2)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
              }}
              onClick={() => setShowNew(true)}
            >
              <PlusCircle size={20} />
              Add New Sub Consent
            </button>
            <button
              style={{
                background: 'rgba(255,255,255,0.2)',
                color: '#fff',
                border: '2px solid rgba(255,255,255,0.3)',
                borderRadius: '12px',
                padding: '0.75rem 1.25rem',
                fontSize: '0.95rem',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                transition: 'all 0.2s ease',
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.25)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
              }}
              onClick={() => (onBack ? onBack() : alert('Going back...'))}
            >
              <ArrowLeft size={18} />
              Back
            </button>
          </div>
        </div>
      </div>

      {/* New consent form modal */}
      {showNew && !isEditing && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1200, display: showNew ? 'block' : 'none' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.45)' }} onClick={() => { setShowNew(false); }} />
          <aside style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: 420, maxWidth: '90vw', background: '#fff', boxShadow: '-4px 0 24px rgba(2,6,23,0.12)', transform: showNew ? 'translateX(0)' : 'translateX(100%)', transition: 'transform 240ms ease', zIndex: 1201, overflow: 'auto', borderRadius: '12px 0 0 12px' }}>
            <div style={{ padding: '1rem 1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #e2e8f0', background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)', color: '#fff' }}>
              <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 700 }}>Create New Sub Consent</h3>
              <button onClick={() => { setShowNew(false); }} style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ padding: 16 }}>
              <div style={styles.modalContent}>
                <label style={{ fontSize: 13, color: '#475569', fontWeight: 600, display: 'block', marginBottom: 6 }}>Sub-Consent Name *</label>
                <input
                  value={newName}
                  onChange={e => setNewName(e.target.value)}
                  placeholder={category ? `${category} - New subcategory` : 'e.g. Visitor Photo Consent'}
                  style={{ width: '100%', padding: '0.75rem', border: '2px solid #e2e8f0', borderRadius: 8, fontSize: '0.95rem', marginBottom: 16 }}
                />

                <label style={{ fontSize: 13, color: '#475569', fontWeight: 600, display: 'block', marginBottom: 6 }}>Consent Content *</label>
                <textarea
                  value={newMessage}
                  onChange={e => setNewMessage(e.target.value)}
                  rows={5}
                  placeholder="Enter consent content here..."
                  style={{ ...styles.textarea, border: '2px solid #e2e8f0', borderRadius: 8, padding: '0.75rem', fontSize: '0.95rem', marginBottom: 16 }}
                />

                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, minWidth: 160 }}>
                    <label style={{ fontSize: 13, color: '#475569', fontWeight: 600, display: 'block', marginBottom: 6 }}>Icon</label>
                    <select value={newIcon} onChange={e => setNewIcon(e.target.value)} style={{ width: '100%', padding: '0.75rem', border: '2px solid #e2e8f0', borderRadius: 8 }}>
                      <option value="FileText">Document</option>
                      <option value="CreditCard">Card</option>
                      <option value="Shield">Shield</option>
                      <option value="List">List</option>
                      <option value="User">User</option>
                      <option value="Mail">Mail</option>
                    </select>
                  </div>
                  <div style={{ flex: 1, minWidth: 160 }}>
                    <label style={{ fontSize: 13, color: '#475569', fontWeight: 600, display: 'block', marginBottom: 6 }}>Version</label>
                    <input value={newVersion} onChange={e => setNewVersion(e.target.value)} placeholder="e.g. 1.0" style={{ width: '100%', padding: '0.75rem', border: '2px solid #e2e8f0', borderRadius: 8 }} />
                  </div>
                  <div>
                    <label style={{ fontSize: 13, color: '#475569', fontWeight: 600, display: 'block', marginBottom: 6 }}>Icon Color</label>
                    <input type="color" value={newColor} onChange={e => setNewColor(e.target.value)} style={{ width: 60, height: 48, border: '2px solid #e2e8f0', borderRadius: 8, cursor: 'pointer' }} />
                  </div>
                  {/* <div style={{ width: 140 }}>
                  <label style={{ fontSize: 13, color: '#475569', fontWeight: 600, display: 'block', marginBottom: 6 }}>Notified (% of users)</label>
                  <input type="number" min="0" max="100" value={newPercent} onChange={e => setNewPercent(Number(e.target.value || 0))} style={{ width: '100%', padding: '0.75rem', border: '2px solid #e2e8f0', borderRadius: 8 }} />
                </div> */}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, padding: 16, borderTop: '1px solid #e2e8f0' }}>
                <button onClick={() => { setShowNew(false); setIsEditing(false); setEditingKey(null); }} style={styles.backBtn}>Cancel</button>
                <button onClick={async () => {
                  try {
                    if (!newName) { alert('Please provide a name'); return; }
                    setSubmitting(true);
                    const payload = {
                      sub_category: newName,
                      name: category || newName,
                      consent_type: category || newName,
                      consent_id: parentConsentId,
                      parent_consent_id: parentConsentId,
                      parent_consent_type: category || newName,
                      parent_consent_label: parentConsentLabel || category || newName,
                      message: newMessage,
                      data: JSON.stringify({
                        kind: 'sub_consent',
                        parent_consent_type: category || newName,
                        parent_consent_label: parentConsentLabel || category || newName,
                        consent_id: parentConsentId,
                        parent_consent_id: parentConsentId,
                        sub_consent_name: newName,
                        icon: newIcon,
                        color: newColor,
                        version: newVersion ? newVersion.trim() : undefined,
                        notified_percent: newPercent,
                      }),
                      tenant_id: tenantId
                    };
                    // const res = await fetch(`${API_BASE}/manager/create-subcategory`, {
                    const url = `${runtimeBase.replace(/\/$/, '')}/manager/create-subcategory`;
                    const headers = { 'Content-Type': 'application/json' };
                    if (token) headers['Authorization'] = `Bearer ${token}`;
                    const res = await fetch(url, {
                      method: 'POST',
                      // headers: {'Content-Type': 'application/json',}
                      headers,
                      body: JSON.stringify(payload)
                    });
                    const body = await res.json().catch(() => ({}));
                    if (!res.ok) {
                      // const msg = body && (body.detail ? body.detail  : 'Failed to create subcategory';
                      const msg = body && (body.detail || body.message) ? (body.detail || body.message) : `Failed to create subcategory (${res.status})`;
                      throw new Error(msg);
                    }
                    // refresh list so the created row appears under the selected `category` + tenant filter
                    await fetchRows();
                    setShowNew(false);
                    setNewName(''); setNewMessage(''); setNewIcon('FileText'); setNewColor('#0070f3'); setNewVersion(''); setNewPercent(0);
                  } catch (e) {
                    console.error(e);
                    await Swal.fire({ icon: 'error', title: 'Save Failed', text: e.message || 'Error creating subcategory' });
                  } finally { setSubmitting(false); }
                }} style={{ ...styles.primaryBtn, opacity: submitting ? 0.7 : 1 }} disabled={submitting}>{submitting ? 'Saving...' : 'Submit Consent'}</button>
              </div>
            </div>
          </aside>
        </div>
      )}

      {/* Read-only view modal */}
      {viewOpen && viewItem && (
        <div style={styles.modalOverlay} onClick={() => setViewOpen(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={{ ...styles.modalHeader, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <FileText size={28} color={viewItem.color || '#0070f3'} />
                <strong style={{ fontSize: 18 }}>{viewItem.sub_category || 'View Sub-Consent'}</strong>
              </div>
              <button style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: 20 }} onClick={() => setViewOpen(false)}>×</button>
            </div>
            <div style={styles.modalContent}>
              {/* <div style={{marginBottom: 12,  color: '#475569'}}>{previewText(viewItem.consent_message || viewItem.message || viewItem.consent || '')}</div> */}
              <div style={{ marginBottom: 12, color: '#475569', maxHeight: '60vh', overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word', paddingRight: 8 }}>
                {String(viewItem.consent_message || viewItem.message || viewItem.consent || 'No message provided.')}
              </div>
              <div style={{ marginBottom: 8, color: '#64748b', fontSize: 13 }}>Parent consent: {viewItem.parent_consent_label || parentConsentLabel || category}</div>
              <div style={{ marginBottom: 8, color: '#64748b', fontSize: 13 }}>Version: v{viewItem.version || '—'}</div>
              <div style={{ marginBottom: 8, color: '#64748b', fontSize: 13 }}>Created: {viewItem.raw?.created_at ? new Date(viewItem.raw.created_at).toLocaleString() : '—'}</div>
            </div>
            <div style={{ ...styles.modalActions }}>
              <button style={styles.backBtn} onClick={() => setViewOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit panel */}
      {showNew && isEditing && (
        <div style={styles.modalOverlay}>
          <div style={{
            ...styles.modal,
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 24px 80px rgba(2,6,23,0.3)'
          }} role="dialog" aria-modal="true">
            <div style={{
              ...styles.modalHeader,
              background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
              color: '#fff',
              padding: '1rem 1.25rem'
            }}>
              <h3 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 700 }}>Edit sub-category</h3>
              <button onClick={() => { setShowNew(false); setIsEditing(false); setEditingKey(null); }} style={{ background: 'transparent', border: 'none', cursor: 'pointer', fontSize: 20, color: '#fff' }}>✖</button>
            </div>
            <div style={styles.modalContent}>
              <label style={{ fontSize: 12, color: '#475569' }}>Name</label>
              <input value={newName} onChange={e => setNewName(e.target.value)} placeholder={category ? `${category} - Subcategory` : 'Sub-category name'} style={{ width: '100%', padding: '0.5rem', border: '1px solid #e2e8f0', borderRadius: 6, marginBottom: 12 }} />
              <label style={{ fontSize: 12, color: '#475569', marginTop: 8 }}>Message / Description</label>
              <textarea value={newMessage} onChange={e => setNewMessage(e.target.value)} rows={5} style={{ ...styles.textarea, marginBottom: 12 }} />
              <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 160 }}>
                  <label style={{ fontSize: 12, color: '#475569' }}>Icon</label>
                  <select value={newIcon} onChange={e => setNewIcon(e.target.value)} style={{ width: '100%', padding: '0.5rem', border: '1px solid #e2e8f0', borderRadius: 6 }}>
                    <option value="FileText">Text</option>
                    <option value="CreditCard">Card</option>
                    <option value="Shield">Shield</option>
                    <option value="List">List</option>
                    <option value="User">User</option>
                    <option value="Mail">Mail</option>
                  </select>
                </div>
                <div style={{ flex: 1, minWidth: 160 }}>
                  <label style={{ fontSize: 12, color: '#475569' }}>Version</label>
                  <input value={newVersion} onChange={e => setNewVersion(e.target.value)} placeholder="e.g. 1.0" style={{ width: '100%', padding: '0.5rem', border: '1px solid #e2e8f0', borderRadius: 6 }} />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: '#475569' }}>Color</label>
                  <input type="color" value={newColor} onChange={e => setNewColor(e.target.value)} style={{ width: 48, height: 34, border: 'none', padding: 0, cursor: 'pointer' }} />
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, padding: 12, borderTop: '1px solid #e2e8f0' }}>
              <button onClick={() => { setShowNew(false); setIsEditing(false); setEditingKey(null); }} style={styles.backBtn}>Cancel</button>
              <button onClick={async () => {
                try {
                  if (!editingKey) return alert('No item selected');
                  if (!window.confirm('Delete this sub-consent? This action cannot be undone.')) return;
                  const idToDelete = editingKey;
                  const url = `${runtimeBase.replace(/\/$/, '')}/manager/sub-category-consents/${idToDelete}?tenant_id=${encodeURIComponent(tenantId)}`;
                  const headers = {};
                  if (token) headers['Authorization'] = `Bearer ${token}`;
                  const res = await fetch(url, { method: 'DELETE', headers });
                  const body = await res.json().catch(() => ({}));
                  if (!res.ok) throw new Error((body && (body.detail || body.message)) || `Delete failed (${res.status})`);
                  // remove locally
                  setRows(prev => (Array.isArray(prev) ? prev.filter(it => (it.id || it.sub_category || it.consent_type || '').toString() !== idToDelete.toString()) : []));
                  setShowNew(false); setIsEditing(false); setEditingKey(null);
                  alert('Deleted');
                } catch (e) {
                  console.error('Delete failed', e);
                  alert(e.message || 'Failed to delete');
                }
              }} style={styles.dangerBtn}>Delete</button>
              <button onClick={async () => {
                try {
                  if (!editingKey) return alert('No item selected');
                  setSubmitting(true);
                  const payload = {
                    id: editingKey,
                    sub_category: newName,
                    name: category || newName,
                    consent_type: category || newName,
                    consent_id: parentConsentId,
                    parent_consent_id: parentConsentId,
                    parent_consent_type: category || newName,
                    parent_consent_label: parentConsentLabel || category || newName,
                    message: newMessage,
                    data: JSON.stringify({
                      kind: 'sub_consent',
                      parent_consent_type: category || newName,
                      parent_consent_label: parentConsentLabel || category || newName,
                      consent_id: parentConsentId,
                      parent_consent_id: parentConsentId,
                      sub_consent_name: newName,
                      icon: newIcon,
                      color: newColor,
                      version: newVersion ? newVersion.trim() : undefined,
                      notified_percent: newPercent,
                    }),
                    tenant_id: tenantId
                  };
                  const url = `${runtimeBase.replace(/\/$/, '')}/manager/update-subcategory`;
                  const headers = { 'Content-Type': 'application/json' };
                  if (token) headers['Authorization'] = `Bearer ${token}`;
                  const res = await fetch(url, {
                    method: 'POST',
                    headers,
                    body: JSON.stringify(payload)
                  });
                  const body = await res.json().catch(() => ({}));
                  if (!res.ok) {
                    const msg = body && (body.detail || body.message) ? (body.detail || body.message) : `Failed to update subcategory (${res.status})`;
                    throw new Error(msg);
                  }
                  await fetchRows();
                  setShowNew(false);
                  setIsEditing(false);
                  setEditingKey(null);
                  setNewName(''); setNewMessage(''); setNewIcon('FileText'); setNewColor('#0070f3'); setNewVersion(''); setNewPercent(0);
                } catch (e) {
                  console.error('Update failed', e);
                  alert(e.message || 'Failed to update subcategory');
                } finally {
                  setSubmitting(false);
                }
              }} style={{ ...styles.primaryBtn, opacity: submitting ? 0.7 : 1 }} disabled={submitting}>{submitting ? 'Saving...' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ padding: '1rem' }}>
        {loading && <div>Loading...</div>}
        {error && <div style={styles.error}>{error}</div>}

        <div style={styles.grid}>
          {rows.map((r, idx) => {
            const key = r.id || r.consent_type || `${category}-${idx}`;
            const color = r.color || '#2563eb';
            const iconName = r.icon || 'FileText';
            const iconMap = { FileText, CreditCard, Shield, List, User, Mail };
            const IconComp = iconMap[iconName] || FileText;
            const isHovered = hoveredKey === key;

            return (
              <div
                key={key}
                style={{ ...styles.card, cursor: 'default' }}
                onMouseEnter={() => setHoveredKey(key)}
                onMouseLeave={() => setHoveredKey(null)}
              >
                <div style={{ ...styles.cardActions, opacity: isHovered ? 1 : 0, transform: isHovered ? 'translateY(0)' : 'translateY(-4px)' }}>
                  <button title="View" type="button" aria-label="view" style={styles.actionSmall} onClick={() => { setViewItem(r); setViewOpen(true); }}>
                    <Eye size={14} />
                  </button>
                  <button
                    title={r.locked ? 'Multilingual' : 'Accept & Lock consent first'}
                    type="button"
                    disabled={!r.locked}
                    aria-label={r.locked ? 'multilingual sub-consent' : 'multilingual sub-consent disabled until accepted'}
                    style={{
                      ...styles.actionSmall,
                      color: r.locked ? '#2563eb' : '#cbd5e1',
                      opacity: r.locked ? 1 : 0.55,
                      cursor: r.locked ? 'pointer' : 'not-allowed',
                    }}
                    onClick={() => {
                      if (!r.locked) return;
                      if (typeof onOpenSubMultilingual === 'function') {
                        onOpenSubMultilingual(r);
                      }
                    }}
                  >
                    <Languages size={14} />
                  </button>
                  {!r.locked && <button title="Edit" type="button" aria-label="edit" style={styles.actionSmall} onClick={() => {
                    const keyId = r.id || r.sub_category || r.consent_type || `${category}-${idx}`;
                    setEditingKey(keyId);
                    setNewName(r.sub_category || r.parsed?.sub_consent_name || r.type || '');
                    setNewMessage(r.consent_message || r.message || r.consent || '');
                    setNewIcon(r.icon || 'FileText');
                    setNewColor(r.color || '#0070f3');
                    setNewVersion(r.version || '');
                    if (r.locked) {
                      Swal.fire({ icon: 'info', title: 'Accepted & Locked', text: 'Create a higher version to make changes.', showCancelButton: true, confirmButtonText: 'Create New Version', confirmButtonColor: '#2563eb' }).then(result => {
                        if (result.isConfirmed) startNextVersion(r);
                      });
                      return;
                    }
                    setIsEditing(true);
                    setShowNew(true);
                  }}>
                    <Edit size={14} />
                  </button>}
                  {!r.locked && <button title="Delete" type="button" aria-label="delete" style={{ ...styles.actionSmall, color: '#dc2626' }} onClick={async () => {
                    const confirmed = await Swal.fire({
                      title: 'Delete sub-consent?',
                      text: 'This action cannot be undone.',
                      icon: 'warning',
                      showCancelButton: true,
                      confirmButtonColor: '#dc2626',
                      cancelButtonColor: '#6b7280',
                      confirmButtonText: 'Delete',
                      cancelButtonText: 'Cancel',
                    });
                    if (!confirmed.isConfirmed) return;
                    try {
                      const url = `${runtimeBase.replace(/\/$/, '')}/manager/sub-category-consents/${r.id}?tenant_id=${encodeURIComponent(tenantId)}`;
                      const headers = { 'Content-Type': 'application/json' };
                      if (token) headers.Authorization = `Bearer ${token}`;
                      const res = await fetch(url, { method: 'DELETE', headers });
                      const body = await res.json().catch(() => ({}));
                      if (!res.ok) throw new Error(body?.detail || body?.message || `Failed to delete (${res.status})`);
                      setRows(prev => prev.filter(item => item.id !== r.id));
                      await Swal.fire({
                        title: 'Deleted',
                        text: 'Sub-consent deleted successfully.',
                        icon: 'success',
                        confirmButtonText: 'OK',
                      });
                    } catch (err) {
                      await Swal.fire({
                        title: 'Delete failed',
                        text: err.message || 'Failed to delete the sub-consent.',
                        icon: 'error',
                        confirmButtonText: 'OK',
                      });
                    }
                  }}>
                    <Trash2 size={14} />
                  </button>}
                  <button
                    title={r.locked ? 'Accepted and locked' : 'Accept & Lock'}
                    type="button"
                    aria-label="accept and lock"
                    style={{ ...styles.actionSmall, color: r.locked ? '#64748b' : '#059669', opacity: r.locked ? 0.65 : 1 }}
                    disabled={Boolean(r.locked)}
                    onClick={() => acceptVersion(r)}
                  >
                    {r.locked ? <Lock size={14} /> : <CheckCircle size={14} />}
                  </button>
                </div>

                <div style={{ ...styles.iconWrapper, background: color + '22', color }}>
                  <IconComp size={34} color={color} />
                </div>

                <div style={styles.cardMeta}>Parent consent: {r.parent_consent_label || parentConsentLabel || category}</div>
                <div style={styles.cardTitle}>{r.sub_category || (r.consent_type ? formatDisplayLabel(r.consent_type) : 'Untitled')}</div>
                <div style={styles.cardDesc}>{previewText(r.consent_message || r.message || r.consent || '')}</div>
                <div style={{ marginTop: 8, textAlign: 'center', color: r.locked ? '#15803d' : '#b45309', fontSize: 12, fontWeight: 700 }}>{r.locked ? 'ACCEPTED & LOCKED' : String(r.status || 'UNDER PROCESS').replace(/_/g, ' ').toUpperCase()}</div>
                <div style={{ marginTop: 5, textAlign: 'center', color: '#64748b', fontSize: 11 }}>Language: {r.language_name || 'English'} · v{r.version || version}</div>
                <div style={{ position: 'absolute', bottom: 8, right: 8 }}>
                  <button type="button" onClick={(e) => { e.stopPropagation(); setVersionItem(r); }} style={{ border: 0, background: 'none', padding: 0, fontSize: '0.85rem', color: '#64748b', cursor: 'pointer' }}>v{r.version || version}</button>
                </div>
                <div style={{ marginTop: 12 }}>
                  {/* <div style={{ height: 8, width: '100%', background: '#eef2ff', borderRadius: 6, overflow: 'hidden' }}>
                    <div style={{ width: `${Math.max(0, Math.min(100, usage))}%`, height: '100%', background: color, transition: 'width 360ms ease' }} />
                  </div> */}
                  {/* <div style={{ marginTop: 6, fontSize: 12, color: '#475569', display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Activity size={12} />
                    <strong style={{ color: '#0f172a' }}>{usage}%</strong>
                    <span style={{ color: '#94a3b8' }}>usage</span>
                  </div> */}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      {versionItem && <VersionManagementModal item={versionItem} mode="sub" tenantId={tenantId} token={token} onClose={() => setVersionItem(null)} onUpdated={fetchRows} />}
    </div>
  );
}

export default SubCategoryConsents;